// frontend/src/components/Layout.jsx
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  LayoutDashboard,
  Building2,
  Users,
  Users2,
  FileText,
  Settings,
  LogOut,
  User,
  BookOpen,
  ClipboardList,
  Shield,
  Network,
} from "lucide-react";

function NavItem({ to, icon: Icon, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-brand-500 text-white"
            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
        }`
      }
    >
      <Icon size={18} />
      {label}
    </NavLink>
  );
}

function NavSection({ label }) {
  return (
    <div className="pt-4 pb-1 px-3">
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
        {label}
      </p>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, isAdmin, isMaster, isVendor, isPartner, logout } = useAuth();
  const navigate = useNavigate();

  function handleLogout() {
    logout();
    navigate("/login");
  }

  // Display name for the sidebar footer
  const displayName = user?.name ?? user?.companyName ?? "?";
  const displayRole =
    user?.userType === "admin"
      ? `${user.role === "MASTER" ? "Master Admin" : "Sub-Access"}${user.scopeType === "SUBSIDIARY" ? " · Scoped" : ""}`
      : user?.userType;

  return (
    <div className="flex h-screen bg-gray-50">
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      <aside className="w-60 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
            Rhaya Flicks
          </p>
          <h1 className="text-lg font-bold text-brand-700 leading-tight mt-0.5">
            Legal CRM
          </h1>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          <NavItem to="/dashboard" icon={LayoutDashboard} label="Dashboard" />

          {/* Admin navigation */}
          {isAdmin && (
            <>
              <NavSection label="Management" />
              <NavItem to="/entities" icon={Building2} label="Entities" />
              <NavItem to="/groups" icon={Network} label="Groups" />
              <NavItem to="/vendors" icon={Users} label="Vendors" />
              <NavItem to="/partners" icon={Users2} label="Partners" />
              <NavItem to="/agreements" icon={FileText} label="Agreements" />
              <NavItem to="/permits" icon={Shield} label="Perizinan" />
              <NavItem to="/proposals" icon={ClipboardList} label="Proposals" />
              <NavItem to="/templates" icon={BookOpen} label="Templates" />

              {isMaster && (
                <>
                  <NavSection label="System" />
                  <NavItem
                    to="/settings/admins"
                    icon={Settings}
                    label="Admin Users"
                  />
                </>
              )}
            </>
          )}

          {/* Vendor navigation */}
          {isVendor && (
            <>
              <NavSection label="My Account" />
              <NavItem
                to={`/vendors/${user.userId}`}
                icon={User}
                label="My Profile"
              />
              <NavItem to="/portal/proposals" icon={ClipboardList} label="Pengajuan" />
              <NavItem to="/agreements" icon={FileText} label="Agreements" />
              <NavItem to="/portal/templates" icon={BookOpen} label="Templates" />
            </>
          )}

          {/* Partner navigation */}
          {isPartner && (
            <>
              <NavSection label="My Account" />
              <NavItem
                to={`/partners/${user.userId}`}
                icon={User}
                label="My Profile"
              />
              <NavItem to="/agreements" icon={FileText} label="Agreements" />
              <NavItem to="/portal/templates" icon={BookOpen} label="Templates" />
            </>
          )}
        </nav>

        {/* User footer */}
        <div className="px-3 py-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50 mb-2">
            <div
              className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center
                            text-white text-sm font-semibold flex-shrink-0"
            >
              {displayName[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {displayName}
              </p>
              <p className="text-xs text-gray-500 capitalize truncate">
                {displayRole}
              </p>
            </div>
          </div>

          <button
            onClick={handleLogout}
            className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600
                       hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Main content ─────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-6 py-8">{children}</div>
      </main>
    </div>
  );
}
